import { Divider } from "@mui/material";
import { CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react";
import { useRouter } from "@tanstack/react-router";
import clsx from "clsx";
import dayjs from "dayjs";
import { Fragment, useState } from "react";
import { useIntl } from "react-intl";

import { Avatar } from "@/components/Avatar/Avatar";
import { Button } from "@/components/Button";
import { Collapse } from "@/components/Collapse/Collapse";
import { useFlightSearchDataContext } from "@/features/flights/contexts/FlightSearchDataContext";
import { TripType, type FlightType } from "@/features/flights/types/types";
import type { FindFlightOffersOfferItemV3 } from "@/types/codegen";
import { parseDuration } from "@/utils/duration";

import FlightOriginAndDestination from "../FlightOriginAndDestination";

import { SegmentDetails } from "./SegmentDetails";
import { StopsLabel } from "./StopsLabel";
import { TimelineDot } from "./TimelineDot";
import { TransferBar } from "./TransferBar";
import {
  dividerStyle,
  getNumberOfCarriersHandlingFlight,
  isStopOvernight,
  mapCabinClasses,
} from "./utils";

type Params = {
  flight: FlightType;
  offerItems?: FindFlightOffersOfferItemV3[];
  isDepartureFlight?: boolean;
};

function FlightDetailsCard({ flight, offerItems, isDepartureFlight }: Params) {
  const { history } = useRouter();
  const intl = useIntl();

  const { searchData } = useFlightSearchDataContext();

  const segments = flight?.segments || [];
  const stops = flight?.stops || [];
  const firstSegment = segments[0];
  const lastSegment = segments[segments.length - 1];
  const numberOfCarriers = getNumberOfCarriersHandlingFlight(flight);
  const firstSegmentDatesDayDifference = dayjs(firstSegment.arrival.date).diff(
    dayjs(firstSegment.departure.date),
    "day",
  );
  const lastSegmentDatesDayDifference = dayjs(lastSegment.arrival.date).diff(
    dayjs(lastSegment.departure.date),
    "day",
  );

  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="section-card pb-2">
      <div className="mb-4 flex items-center gap-2">
        <div className="relative">
          <Avatar
            alt={intl.formatMessage(
              {
                id: "flights.airlineLogoAlt",
                defaultMessage: "{airline} logo",
              },
              {
                airline:
                  firstSegment.marketingCarrier.airline.name ||
                  firstSegment.marketingCarrier.airline.code,
              },
            )}
            size="large"
            src={firstSegment.marketingCarrier.airline.logoUrl}
          />
          {numberOfCarriers > 1 ? (
            <div className="absolute right-[-4px] bottom-[-4px] flex size-5 items-center justify-center rounded-full border-2 border-white bg-gray-200 text-[10px] text-black">
              {numberOfCarriers - 1}+
            </div>
          ) : null}
        </div>
        <div className="flex flex-col justify-between">
          <FlightOriginAndDestination flight={flight} className="font-medium" />
          <div className="text-sm text-text-tertiary">
            {intl.formatMessage(
              { id: "durationValue", defaultMessage: "{hours}h {minutes}m" },
              parseDuration(flight.duration),
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-[max-content_min-content_auto]">
        <div className="pb-4">
          <h3 className="text-end font-medium text-text-title">
            {firstSegment.departure?.time}
          </h3>
          <div className="text-end text-sm text-text-tertiary">
            {firstSegment.departure?.date
              ? dayjs(firstSegment.departure.date).format("ddd, DD MMM")
              : null}
          </div>
        </div>

        {/* dot */}
        <div className="px-4 pt-3">
          <TimelineDot />
        </div>

        <div className="pb-4">
          <div className="flex items-center gap-1 font-medium">
            <h3 className="text-text-title">
              {firstSegment.departure?.city.name}
            </h3>
            <div className="size-[3px] rounded-full bg-text-primary" />
            <h3 className="text-text-title">
              {firstSegment.departure?.airport.code}
            </h3>
          </div>
          <div className="text-sm text-text-tertiary">
            {firstSegment.departure?.airport.name}
          </div>
        </div>

        {/* EXPANDABLE FLIGHT STOPS INFO */}
        <Collapse in={!isExpanded}>
          <div className="pb-4" />
        </Collapse>
        <Collapse in={!isExpanded}>
          <div
            className={clsx("px-4", {
              "-my-8": flight.stops && flight.stops.length > 0,
              "-my-1":
                !flight.stops || (flight.stops && flight.stops.length === 0),
            })}
          >
            <Divider
              orientation="vertical"
              sx={{
                ...dividerStyle,
                minHeight:
                  flight.stops && flight.stops.length > 0 ? "92px" : "52px",
              }}
            />
          </div>
        </Collapse>
        <Collapse in={!isExpanded}>
          <StopsLabel
            stops={flight.stops}
            firstStopCity={firstSegment.arrival.city.name}
          />
        </Collapse>

        {/* START OF EXPANDABLE ROW */}
        {flight.stops && flight.stops.length > 0 ? (
          flight.segments.map((segment, segmentIndex) => {
            const cabinClasses = mapCabinClasses(segment, offerItems);
            const areCarriersDiffer =
              segment.operatingCarrier.airline.code !==
              segment.marketingCarrier.airline.code;
            const segmentDepartureAndArrivalDayDifference = dayjs(
              segment.arrival.date,
            ).diff(dayjs(segment.departure.date), "day");

            const dividerMinHeight = `${(flight.stops && flight.stops.length > 1 ? 183 : 163) + (areCarriersDiffer ? 44 : 0) + (segmentDepartureAndArrivalDayDifference > 0 ? 24 : 0)}px`;

            return (
              <Fragment key={segment.key}>
                {/* EVERY SEGMENT EXCEPT FIRST */}
                {segmentIndex === 0 ? null : (
                  <>
                    <Collapse in={isExpanded}>
                      <div className="pb-4">
                        <h3 className="text-end font-medium text-text-title">
                          {segment.departure?.time}
                        </h3>
                        <div className="text-end text-sm text-text-tertiary">
                          {segment.departure?.date
                            ? dayjs(segment.departure.date).format(
                                "ddd, DD MMM",
                              )
                            : null}
                        </div>
                      </div>
                    </Collapse>
                    {/* dot */}
                    <Collapse in={isExpanded}>
                      <div className="px-4 pt-3">
                        <TimelineDot />
                      </div>
                    </Collapse>
                    <Collapse in={isExpanded}>
                      <div>
                        <div className="flex items-center gap-1 font-medium">
                          <h3 className="text-text-title">
                            {segment.departure.city.name}
                          </h3>
                          <div className="size-[3px] rounded-full bg-text-primary" />
                          <h3 className="text-text-title">
                            {segment.departure.airport.code}
                          </h3>
                        </div>
                        <div className="text-sm text-text-tertiary">
                          {segment.departure.airport.name}
                        </div>
                      </div>
                    </Collapse>
                    {segmentIndex === flight.segments.length - 1 ? (
                      <>
                        <Collapse in={isExpanded}>
                          <div />
                        </Collapse>
                        <Collapse in={isExpanded}>
                          <div className="-my-[43px] px-4 pb-8">
                            <Divider
                              orientation="vertical"
                              sx={{
                                ...dividerStyle,
                                minHeight: dividerMinHeight,
                              }}
                            />
                          </div>
                        </Collapse>
                        <Collapse in={isExpanded}>
                          <div className="pb-4">
                            <SegmentDetails
                              segment={segment}
                              cabinClasses={cabinClasses}
                              isOvernight={
                                segmentDepartureAndArrivalDayDifference > 0
                              }
                            />
                          </div>
                        </Collapse>
                      </>
                    ) : null}
                  </>
                )}
                {/* EVERY SEGMENT EXCEPT LAST */}
                {segmentIndex === flight.segments.length - 1 ? null : (
                  <>
                    <Collapse in={isExpanded}>
                      <div className="pb-4" />
                    </Collapse>
                    <Collapse in={isExpanded}>
                      <div className="-my-[43px] px-4 pb-8">
                        <Divider
                          orientation="vertical"
                          sx={{
                            ...dividerStyle,
                            minHeight: dividerMinHeight,
                          }}
                        />
                      </div>
                    </Collapse>
                    <Collapse in={isExpanded}>
                      <div className="pb-4">
                        <SegmentDetails
                          segment={segment}
                          cabinClasses={cabinClasses}
                          isOvernight={
                            segmentDepartureAndArrivalDayDifference > 0
                          }
                        />
                      </div>
                    </Collapse>
                    <Collapse in={isExpanded}>
                      <div className="pb-4">
                        <h3 className="text-end font-medium text-text-title">
                          {segment.arrival?.time}
                        </h3>
                        <div
                          className={clsx("text-end text-sm", {
                            "text-semantic-error-strong":
                              segmentDepartureAndArrivalDayDifference > 0,
                            "text-text-tertiary":
                              segmentDepartureAndArrivalDayDifference === 0,
                          })}
                        >
                          {segment.arrival?.date ? (
                            <span>
                              {dayjs(segment.arrival.date).format(
                                "ddd, DD MMM",
                              )}
                              {segmentDepartureAndArrivalDayDifference > 0 ? (
                                <sup>
                                  +{segmentDepartureAndArrivalDayDifference}
                                </sup>
                              ) : null}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </Collapse>
                    {/* dot */}
                    <Collapse in={isExpanded}>
                      <div className="px-4 pt-3 pb-4">
                        <TimelineDot />
                      </div>
                    </Collapse>
                    <Collapse in={isExpanded}>
                      <div className="pb-4">
                        <div className="flex items-center gap-1 font-medium">
                          <h3 className="text-text-title">
                            {segment.arrival.city.name}
                          </h3>
                          <div className="size-[3px] rounded-full bg-text-primary" />
                          <h3 className="text-text-title">
                            {segment.arrival.airport.code}
                          </h3>
                        </div>
                        <div className="text-sm text-text-tertiary">
                          {segment.arrival.airport.name}
                        </div>
                      </div>
                    </Collapse>

                    {stops[segmentIndex]?.stopTime && (
                      <Collapse in={isExpanded} className="col-span-3 pb-4">
                        <TransferBar
                          stopTime={stops[segmentIndex].stopTime}
                          cityName={segment.arrival.city.name}
                          isOvernight={isStopOvernight(
                            dayjs(
                              `${segment.arrival.date}, ${segment.arrival.time}`,
                            ),
                            parseDuration(stops[segmentIndex]?.stopTime),
                          )}
                        />
                      </Collapse>
                    )}
                  </>
                )}
              </Fragment>
            );
          })
        ) : (
          <>
            <Collapse in={isExpanded}>
              <div className="pb-4" />
            </Collapse>
            <Collapse in={isExpanded}>
              <div className="-my-4 px-4 pb-4">
                <Divider
                  orientation="vertical"
                  sx={{
                    ...dividerStyle,
                    minHeight: `${
                      148 +
                      (firstSegment.marketingCarrier.airline.code ===
                      firstSegment.operatingCarrier.airline.code
                        ? 0
                        : 44) +
                      (firstSegmentDatesDayDifference > 0 ? 24 : 0)
                    }px`,
                  }}
                />
              </div>
            </Collapse>
            <Collapse in={isExpanded}>
              <div className="pb-4">
                <SegmentDetails
                  segment={firstSegment}
                  cabinClasses={mapCabinClasses(firstSegment, offerItems)}
                  isOvernight={firstSegmentDatesDayDifference > 0}
                />
              </div>
            </Collapse>
          </>
        )}
        {/* END OF EXPANDABLE ROW */}

        <div className="pb-4">
          <h3 className="text-end font-medium text-text-title">
            {lastSegment.arrival?.time}
          </h3>
          <div
            className={clsx("text-end text-sm", {
              "text-semantic-error-strong": lastSegmentDatesDayDifference > 0,
              "text-text-tertiary": lastSegmentDatesDayDifference === 0,
            })}
          >
            {lastSegment.arrival?.date ? (
              <span>
                {dayjs(lastSegment.arrival.date).format("ddd, DD MMM")}
                {dayjs(lastSegment.arrival.date).diff(
                  dayjs(lastSegment.departure.date),
                  "day",
                ) > 0 ? (
                  <sup>
                    +
                    {dayjs(lastSegment.arrival.date).diff(
                      dayjs(firstSegment.departure.date),
                      "day",
                    )}
                  </sup>
                ) : null}
              </span>
            ) : null}
          </div>
        </div>

        {/* dot */}
        <div className="px-4 pb-4">
          <TimelineDot align="bottom" />
        </div>

        <div className="pb-4">
          <div className="flex items-center gap-1 font-medium">
            <h3 className="text-text-title">
              {lastSegment.arrival?.city.name}
            </h3>
            <div className="size-[3px] rounded-full bg-text-primary" />
            <h3 className="text-text-title">
              {lastSegment.arrival?.airport.code}
            </h3>
          </div>
          <div className="text-sm text-text-tertiary">
            {lastSegment.arrival?.airport.name}
          </div>
        </div>
      </div>
      <Divider sx={{ borderColor: "var(--color-stroke-neutral-softer)" }} />
      <div className="mt-2 flex justify-between py-2.5">
        <Button
          type="button"
          variant="textFit"
          size="small"
          onClick={() =>
            history.go(
              searchData?.tripType === TripType.ROUND_TRIP && isDepartureFlight
                ? -2
                : -1,
            )
          }
        >
          {intl.formatMessage({
            id: "flights.changeFlight",
            defaultMessage: "Change flight",
          })}
        </Button>

        <Button
          type="button"
          variant="textFit"
          size="small"
          startIcon={
            isExpanded ? <CaretUpIcon size={16} /> : <CaretDownIcon size={16} />
          }
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          {isExpanded
            ? intl.formatMessage({
                id: "hideDetails",
                defaultMessage: "Hide details",
              })
            : intl.formatMessage({
                id: "showDetails",
                defaultMessage: "Show details",
              })}
        </Button>
      </div>
    </div>
  );
}

export default FlightDetailsCard;
